import * as THREE from "three";
import Orchestrator from "../../Orchestrator.js";
import { CONFIG } from "../config.js";
import { meshChunk } from "./ChunkMesher.js";
import {
    CHUNK_COUNT,
    CHUNK_SIZE,
    MAX_QUADS,
    MIN_Y,
    SKIRT,
    WORLD_SIZE,
} from "./constants.js";

function pickLod(dist, current) {
    const rings = CONFIG.LOD_RINGS;
    if (current !== null && current !== undefined) {
        const finer = rings[current - 1];
        if (finer && dist <= finer.enter) return current - 1;
        if (dist <= rings[current].leave) return current;
    }
    for (let i = 0; i < rings.length; i++) {
        if (dist <= rings[i].enter) return i;
    }
    return rings.length - 1;
}

// Neighbour offsets in the order the mesher reads edges: east, west, north, south.
const SIDES = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
];

function sameEdges(a, b) {
    if (!a || !b) return false;
    return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

function createSharedIndex(maxQuads) {
    const indices = new Uint32Array(maxQuads * 6);
    for (let q = 0; q < maxQuads; q++) {
        const vertex = q * 4;
        const offset = q * 6;
        indices[offset] = vertex;
        indices[offset + 1] = vertex + 1;
        indices[offset + 2] = vertex + 2;
        indices[offset + 3] = vertex;
        indices[offset + 4] = vertex + 2;
        indices[offset + 5] = vertex + 3;
    }
    return new THREE.BufferAttribute(indices, 1);
}

function chunksTouching(i, j) {
    const cx = Math.floor(i / CHUNK_SIZE);
    const cz = Math.floor(j / CHUNK_SIZE);
    const west = i % CHUNK_SIZE === 0;
    const east = i % CHUNK_SIZE === CHUNK_SIZE - 1;
    const south = j % CHUNK_SIZE === 0;
    const north = j % CHUNK_SIZE === CHUNK_SIZE - 1;
    const list = [[cx, cz]];
    if (west) list.push([cx - 1, cz]);
    if (east) list.push([cx + 1, cz]);
    if (south) list.push([cx, cz - 1]);
    if (north) list.push([cx, cz + 1]);
    if (west && south) list.push([cx - 1, cz - 1]);
    if (west && north) list.push([cx - 1, cz + 1]);
    if (east && south) list.push([cx + 1, cz - 1]);
    if (east && north) list.push([cx + 1, cz + 1]);
    return list.filter(
        ([x, z]) => x >= 0 && z >= 0 && x < CHUNK_COUNT && z < CHUNK_COUNT,
    );
}

export default class ChunkManager {
    constructor(data, terrainMaterial, pool) {
        this.orc = new Orchestrator();
        this.data = data;
        this.terrainMaterial = terrainMaterial;
        this.pool = pool;
        this.group = new THREE.Group();
        this.group.name = "terrain";
        this.sharedIndex = createSharedIndex(MAX_QUADS);
        this.chunks = new Map();
        this.focus = { cx: 8, cz: 8 };
        this.spawn = { cx: 8, cz: 8 };
        this.readyEmitted = false;

        for (let cz = 0; cz < CHUNK_COUNT; cz++) {
            for (let cx = 0; cx < CHUNK_COUNT; cx++) {
                this.chunks.set(`${cx},${cz}`, {
                    cx,
                    cz,
                    mesh: null,
                    scale: null,
                    lod: null,
                    token: 0,
                    pendingScale: null,
                    edges: null,
                    pendingEdges: null,
                });
            }
        }
    }

    setSpawn(x, z) {
        this.spawn.cx = Math.min(CHUNK_COUNT - 1, Math.max(0, Math.floor(x / CHUNK_SIZE)));
        this.spawn.cz = Math.min(CHUNK_COUNT - 1, Math.max(0, Math.floor(z / CHUNK_SIZE)));
        this.focus.cx = this.spawn.cx;
        this.focus.cz = this.spawn.cz;
    }

    applyGenerated(heights, baseHeights) {
        this.data.replace(heights, baseHeights);
        this.terrainMaterial.setBaseHeights(baseHeights);
        this.pool.broadcastHeights(this.data.heights, WORLD_SIZE);
        for (const chunk of this.chunks.values()) {
            chunk.token++;
            chunk.pendingScale = null;
            chunk.pendingEdges = null;
            chunk.scale = null;
            chunk.edges = null;
        }
        this.syncLods(true);
    }

    update(camera) {
        const half = WORLD_SIZE / 2;
        const i = Math.round(camera.position.x + half);
        const j = Math.round(camera.position.z + half);
        const cx = Math.min(CHUNK_COUNT - 1, Math.max(0, Math.floor(i / CHUNK_SIZE)));
        const cz = Math.min(CHUNK_COUNT - 1, Math.max(0, Math.floor(j / CHUNK_SIZE)));
        this.focus.cx = cx;
        this.focus.cz = cz;
        this.syncLods(false);
    }

    syncLods(force) {
        const { cx, cz } = this.focus;
        const targets = this.targetLods(force);

        for (const chunk of this.chunks.values()) {
            const index = chunk.cz * CHUNK_COUNT + chunk.cx;
            const dist = Math.max(Math.abs(chunk.cx - cx), Math.abs(chunk.cz - cz));
            const lod = targets.lods[index];
            const scale = targets.scales[index];
            const edges = this.finerEdges(chunk, scale, targets.scales);
            if (
                !force &&
                chunk.mesh &&
                chunk.scale === scale &&
                sameEdges(chunk.edges, edges)
            ) {
                continue;
            }
            if (
                !force &&
                chunk.pendingScale === scale &&
                sameEdges(chunk.pendingEdges, edges)
            ) {
                continue;
            }
            this.request(chunk, scale, lod, dist, edges);
        }
    }

    targetLods(force) {
        const { cx, cz } = this.focus;
        const lods = new Int8Array(CHUNK_COUNT * CHUNK_COUNT);
        const scales = new Uint8Array(CHUNK_COUNT * CHUNK_COUNT);
        for (const chunk of this.chunks.values()) {
            const index = chunk.cz * CHUNK_COUNT + chunk.cx;
            const dist = Math.max(Math.abs(chunk.cx - cx), Math.abs(chunk.cz - cz));
            const lod = pickLod(dist, force ? null : chunk.lod);
            lods[index] = lod;
            scales[index] = CONFIG.LOD_RINGS[lod].scale;
        }
        return { lods, scales };
    }

    // Per side [east, west, north, south]: the neighbour's scale when it is finer
    // than this chunk, else 0. The neighbour counts as finer if its mesh on screen
    // or the one on its way is finer, so there is no crack while either side swaps.
    finerEdges(chunk, scale, targetScales) {
        const edges = [0, 0, 0, 0];
        if (scale <= 1) return edges;
        for (let side = 0; side < 4; side++) {
            const nx = chunk.cx + SIDES[side][0];
            const nz = chunk.cz + SIDES[side][1];
            if (nx < 0 || nz < 0 || nx >= CHUNK_COUNT || nz >= CHUNK_COUNT) continue;
            const neighbour = this.chunks.get(`${nx},${nz}`);
            let neighbourScale = targetScales[nz * CHUNK_COUNT + nx];
            if (neighbour.mesh && neighbour.scale < neighbourScale) {
                neighbourScale = neighbour.scale;
            }
            if (neighbourScale < scale) edges[side] = neighbourScale;
        }
        return edges;
    }

    request(chunk, scale, lod, dist, edges) {
        chunk.token++;
        chunk.pendingScale = scale;
        chunk.pendingEdges = edges;
        const token = chunk.token;
        this.pool
            .mesh({
                worldSize: WORLD_SIZE,
                chunkSize: CHUNK_SIZE,
                cx: chunk.cx,
                cz: chunk.cz,
                scale,
                minY: MIN_Y,
                skirt: SKIRT,
                edges,
                token,
                dist,
            })
            .then((result) => {
                if (chunk.token !== result.token) return;
                if (result.error || !result.positions) {
                    console.error(result.error || "Chunk mesh failed");
                    return;
                }
                chunk.pendingScale = null;
                chunk.pendingEdges = null;
                this.upload(chunk, result, scale, lod, edges);
                this.checkReady();
            });
    }

    remeshColumns(i, j) {
        const list = chunksTouching(i, j);
        for (let n = 0; n < list.length; n++) {
            this.remeshSync(list[n][0], list[n][1]);
        }
    }

    remeshAround(pairs) {
        const seen = new Set();
        for (let n = 0; n < pairs.length; n++) {
            const [cx, cz] = pairs[n];
            for (let dz = -1; dz <= 1; dz++) {
                for (let dx = -1; dx <= 1; dx++) {
                    const nx = cx + dx;
                    const nz = cz + dz;
                    if (nx < 0 || nz < 0 || nx >= CHUNK_COUNT || nz >= CHUNK_COUNT) continue;
                    const key = `${nx},${nz}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    this.remeshSync(nx, nz);
                }
            }
        }
    }

    remeshSync(cx, cz) {
        const chunk = this.chunks.get(`${cx},${cz}`);
        if (!chunk) return;
        chunk.token++;
        chunk.pendingScale = null;
        chunk.pendingEdges = null;
        const scale = chunk.scale || CONFIG.LOD_RINGS[0].scale;
        const lod = chunk.lod ?? 0;
        const edges = chunk.edges ?? [0, 0, 0, 0];
        const built = meshChunk(this.data.mips, {
            worldSize: WORLD_SIZE,
            chunkSize: CHUNK_SIZE,
            cx,
            cz,
            scale,
            minY: MIN_Y,
            skirt: SKIRT,
            edges,
        });
        this.upload(chunk, { ...built, token: chunk.token }, scale, lod, edges);
    }

    upload(chunk, built, scale, lod, edges) {
        const geometry = this.makeGeometry(built);
        if (!chunk.mesh) {
            const mesh = new THREE.Mesh(geometry, this.terrainMaterial.material);
            mesh.name = `chunk-${chunk.cx}-${chunk.cz}`;
            mesh.position.set(
                chunk.cx * CHUNK_SIZE - WORLD_SIZE / 2 - 0.5,
                -0.5,
                chunk.cz * CHUNK_SIZE - WORLD_SIZE / 2 - 0.5,
            );
            this.group.add(mesh);
            chunk.mesh = mesh;
        } else {
            const previous = chunk.mesh.geometry;
            chunk.mesh.geometry = geometry;
            previous.index = null;
            previous.dispose();
        }
        chunk.scale = scale;
        chunk.lod = lod;
        chunk.edges = edges;
    }

    makeGeometry(built) {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Int16BufferAttribute(built.positions, 3));
        geometry.setAttribute("aPacked", new THREE.Uint8BufferAttribute(built.packed, 4));
        geometry.setIndex(this.sharedIndex);
        geometry.setDrawRange(0, built.quadCount * 6);
        geometry.boundingBox = new THREE.Box3(
            new THREE.Vector3(-1, built.minStored - 1, -1),
            new THREE.Vector3(CHUNK_SIZE + 1, built.maxStored + 1, CHUNK_SIZE + 1),
        );
        geometry.boundingSphere = geometry.boundingBox.getBoundingSphere(new THREE.Sphere());
        return geometry;
    }

    checkReady() {
        if (this.readyEmitted) return;
        const { cx, cz } = this.spawn;
        for (let dz = -2; dz <= 2; dz++) {
            for (let dx = -2; dx <= 2; dx++) {
                const chunk = this.chunks.get(`${cx + dx},${cz + dz}`);
                if (chunk && !chunk.mesh) return;
            }
        }
        this.readyEmitted = true;
        this.orc.emitter.emit("terrain-ready");
    }
}
